import {
  buildAbortDescription,
  buildAbortMaterialRows,
  defaultAbortStatus,
  getAbortReasonOptions,
  getAbortStatusOptions,
} from '@/modules/cleanroom/components/abort/abort-material-utils'
import {closeAbortCleanroomDialog} from '@/modules/cleanroom/components/abort/util'
import {CleanroomSequenceOrderData} from '@/modules/cleanroom/hooks/use-cleanroom-sequence-batches'
import {ScannableItem} from '@/modules/cleanroom/state-machine'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Box, Button, ButtonGroup, RadioCard, Table, Text, VStack} from '@chakra-ui/react'
import {AbortMaterialStatus, AbortProductionSchema} from '@cypro/engine/mutators'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {revalidateLogic, useStore} from '@tanstack/react-form'
import {useNavigate} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'
import {LuCircleX, LuRefreshCw} from 'react-icons/lu'

export interface AbortCleanroomDialogProps {
  sequence: PatientTherapySequence
  order: CleanroomSequenceOrderData
  scannableItems: ScannableItem[]
  /** Called after a successful abort instead of the default cleanroom navigation (drawer usage). */
  onAborted?: () => void
  /** The drawer's cancel-production cancels the sequence; the cleanroom abort discards it. */
  cancel?: boolean
}

export function AbortCleanroomDialog({sequence, order, scannableItems, onAborted, cancel}: AbortCleanroomDialogProps) {
  const {t} = useTranslate('cleanroom')
  const {t: tCommon} = useTranslate('common')
  const zero = useZero()
  const navigate = useNavigate()

  const rows = useMemo(() => buildAbortMaterialRows(scannableItems, order), [scannableItems, order])
  const reasonItems = useMemo(() => getAbortReasonOptions(tCommon), [tCommon])

  const form = useAppForm({
    defaultValues: {
      sequenceId: sequence.id,
      reason: '' as AbortProductionSchema['reason'],
      remake: false,
      cancel: cancel ?? false,
      materials: rows.map(row => ({
        itemId: row.itemId,
        kind: row.kind,
        status: defaultAbortStatus(row.isActiveSubstance),
        ...(row.initialWeightVolume != null ? {remainingWeightVolume: row.initialWeightVolume} : {}),
      })),
    },
    validators: {
      onDynamic: AbortProductionSchema,
    },
    validationLogic: revalidateLogic(),
    onSubmit: async ({value}) => {
      await zero.attemptMutation(zero.mutators.patientsTherapies.abortProduction(value), async mutation => {
        const res = await mutation.server
        if (res.type === 'error') return

        toaster.success({title: t('success'), description: t('production_aborted')})
        closeAbortCleanroomDialog()

        if (onAborted) {
          onAborted()
          return
        }

        navigate({
          to: '.',
          from: '/cleanroom/',
          search: prev => ({...prev, sequenceId: undefined}),
          ignoreBlocker: true,
        })
      })
    },
  })

  const materialStatuses = useStore(form.store, state => state.values.materials.map(m => m.status))

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <form.ErrorObserver />

        <DialogHeader description={buildAbortDescription(sequence, t)}>{t('abort_preparation')}</DialogHeader>

        <DialogBody minHeight="none" overflowY="auto">
          <VStack gapY="6" width="100%" alignItems="stretch">
            <form.AppField name="remake" mode="value">
              {field => (
                <RadioCard.Root
                  value={field.state.value ? 'remake' : 'cancel'}
                  onValueChange={e => field.handleChange(e.value === 'remake')}
                >
                  <VStack align="stretch">
                    <RadioCard.Item value="cancel">
                      <RadioCard.ItemHiddenInput />
                      <RadioCard.ItemControl>
                        <Box
                          display={'flex'}
                          flexDirection={'row'}
                          justifyContent={'flex-start'}
                          gap={2}
                          padding={2}
                          width={'100%'}
                        >
                          <LuCircleX size={24} />
                          <Box>
                            <Text fontWeight={'medium'} fontSize={'sm'}>
                              {tCommon('abort_dialog.cancel_order.title')}
                            </Text>
                            <Text fontWeight={'normal'} fontSize={'sm'} color="fg.muted">
                              {tCommon('abort_dialog.cancel_order.description')}
                            </Text>
                          </Box>
                        </Box>
                        <RadioCard.ItemIndicator />
                      </RadioCard.ItemControl>
                    </RadioCard.Item>
                    <RadioCard.Item value="remake">
                      <RadioCard.ItemHiddenInput />
                      <RadioCard.ItemControl>
                        <Box
                          display={'flex'}
                          flexDirection={'row'}
                          justifyContent={'flex-start'}
                          gap={2}
                          padding={2}
                          width={'100%'}
                        >
                          <LuRefreshCw size={24} />
                          <Box>
                            <Text fontWeight={'medium'} fontSize={'sm'}>
                              {tCommon('abort_dialog.remake_order.title')}
                            </Text>
                            <Text fontWeight={'normal'} fontSize={'sm'} color="fg.muted">
                              {tCommon('abort_dialog.remake_order.description')}
                            </Text>
                          </Box>
                        </Box>
                        <RadioCard.ItemIndicator />
                      </RadioCard.ItemControl>
                    </RadioCard.Item>
                  </VStack>
                </RadioCard.Root>
              )}
            </form.AppField>

            <form.AppField name="reason" mode="value">
              {field => (
                <field.Select
                  label={t('abort_reason_label')}
                  placeholder={tCommon('eg', {value: reasonItems?.[0]?.label})}
                  items={reasonItems}
                  required
                />
              )}
            </form.AppField>

            <Box spaceY="3" width="100%">
              <Box spaceY="1">
                <Text textStyle="sm" fontWeight="medium">
                  {t('abort_material_report')}
                </Text>
                <Text textStyle="xs" color="fg.muted">
                  {t('abort_material_report_description')}
                </Text>
              </Box>

              {/* <Table.ScrollArea maxHeight="xl" width="full"> */}
              <Table.Root size="sm">
                <Table.Header bg="bg.panel">
                  <Table.Row>
                    <Table.ColumnHeader width="50%">{t('abort_material')}</Table.ColumnHeader>
                    <Table.ColumnHeader width="25%">{t('abort_material_status')}</Table.ColumnHeader>
                    <Table.ColumnHeader width="25%">{t('abort_weight_volume')}</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {rows.map((row, index) => {
                    const statusItems = getAbortStatusOptions(t, row.isActiveSubstance, row.kind)
                    const isPartiallyConsumed = materialStatuses[index] === AbortMaterialStatus.enum.PARTIALLY_CONSUMED
                    const border = index === rows.length - 1 ? 'none' : undefined

                    return (
                      <Table.Row key={row.key}>
                        <Table.Cell border={border}>{row.name}</Table.Cell>
                        <Table.Cell border={border}>
                          <form.AppField
                            name={`materials[${index}].status`}
                            mode="value"
                            listeners={{
                              onChange: ({value}) => {
                                if (
                                  row.isActiveSubstance &&
                                  (value === AbortMaterialStatus.enum.UNUSED ||
                                    value === AbortMaterialStatus.enum.SCRAPPED)
                                ) {
                                  form.setFieldValue(
                                    `materials[${index}].remainingWeightVolume`,
                                    row.initialWeightVolume
                                  )
                                }
                              },
                            }}
                          >
                            {field => (
                              <field.Select
                                label={t('abort_material_status')}
                                placeholder={tCommon('eg', {value: statusItems[0]?.label})}
                                items={statusItems}
                                hideLabel
                              />
                            )}
                          </form.AppField>
                        </Table.Cell>
                        <Table.Cell border={border}>
                          {row.trackRemainingWeightVolume && (
                            <form.AppField name={`materials[${index}].remainingWeightVolume`}>
                              {field => (
                                <field.NumberInput
                                  label={t('abort_weight_volume')}
                                  hideLabel
                                  disabled={!isPartiallyConsumed}
                                  min={0}
                                  max={row.initialWeightVolume}
                                />
                              )}
                            </form.AppField>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table.Root>
              {/* </Table.ScrollArea> */}
            </Box>
          </VStack>
        </DialogBody>

        <DialogFooter>
          <ButtonGroup size="md" colorPalette="red" width="100%" justifyContent="flex-end">
            <Button variant="outline" colorPalette="gray" onClick={closeAbortCleanroomDialog}>
              {t('cancel')}
            </Button>
            <form.SubmitButton colorPalette="red">{t('confirm_abort')}</form.SubmitButton>
          </ButtonGroup>
        </DialogFooter>
      </form.AppForm>
    </form>
  )
}
